import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import { getWorkspaceAgentClientMessageId } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentMessageStore';

const OPTIMISTIC_MESSAGE_ID_PREFIX = 'temp:';

export const WORKSPACE_AGENT_LIVE_ANNOUNCEMENT_USER_PREFIX = 'You said: ';
export const WORKSPACE_AGENT_LIVE_ANNOUNCEMENT_AGENT_PREFIX = 'Agent said: ';

export type WorkspaceAgentLiveAnnouncementUpdate =
  | { action: 'noop'; nextLastMessageId: string | null; nextMessageCount: number }
  | { action: 'seed'; nextLastMessageId: string; nextMessageCount: number }
  | { action: 'announce'; announcement: string; nextLastMessageId: string; nextMessageCount: number };

export type GetWorkspaceAgentLiveAnnouncementUpdateParams = {
  isLoadingMessages: boolean;
  messages: readonly WorkspaceAgentMessage[];
  previousLastMessageId: string | null;
  previousMessageCount: number;
  shouldSeedSessionBaseline: boolean;
  wasLoadingMessages: boolean;
};

export type ResolveWorkspaceAgentLiveAnnouncementShouldSeedSessionBaselineParams = {
  isLoadingMessages: boolean;
  messageCount: number;
  shouldSeedSessionBaseline: boolean;
  wasLoadingMessages: boolean;
};

const isWorkspaceAgentLiveAnnouncementRole = (role: WorkspaceAgentMessage['role']): boolean =>
  role === 'user' || role === 'assistant';

export const getLastWorkspaceAgentLiveAnnouncementMessage = (
  messages: readonly WorkspaceAgentMessage[],
): WorkspaceAgentMessage | undefined =>
  messages.findLast((message) => isWorkspaceAgentLiveAnnouncementRole(message.role));

export const buildWorkspaceAgentLiveAnnouncement = (message: WorkspaceAgentMessage): string => {
  const prefix =
    message.role === 'user'
      ? WORKSPACE_AGENT_LIVE_ANNOUNCEMENT_USER_PREFIX
      : WORKSPACE_AGENT_LIVE_ANNOUNCEMENT_AGENT_PREFIX;

  return `${prefix}${message.content ?? ''}`;
};

const isOptimisticUserMessageHandoff = ({
  previousLastMessageId,
  nextMessage,
}: {
  previousLastMessageId: string;
  nextMessage: WorkspaceAgentMessage;
}): boolean => {
  if (!previousLastMessageId.startsWith(OPTIMISTIC_MESSAGE_ID_PREFIX)) {
    return false;
  }

  if (nextMessage.role !== 'user' || nextMessage.isPending) {
    return false;
  }

  const optimisticClientMessageId = previousLastMessageId.slice(OPTIMISTIC_MESSAGE_ID_PREFIX.length);
  const confirmedClientMessageId = getWorkspaceAgentClientMessageId(nextMessage.metadata);

  if (!optimisticClientMessageId || !confirmedClientMessageId) {
    return false;
  }

  return optimisticClientMessageId === confirmedClientMessageId;
};

export const resolveWorkspaceAgentLiveAnnouncementShouldSeedSessionBaseline = ({
  isLoadingMessages,
  messageCount,
  shouldSeedSessionBaseline,
  wasLoadingMessages,
}: ResolveWorkspaceAgentLiveAnnouncementShouldSeedSessionBaselineParams): boolean => {
  if (!shouldSeedSessionBaseline) {
    return false;
  }

  if (!isLoadingMessages && messageCount === 0 && !wasLoadingMessages) {
    return false;
  }

  return true;
};

export const getWorkspaceAgentLiveAnnouncementUpdate = ({
  isLoadingMessages,
  messages,
  previousLastMessageId,
  previousMessageCount,
  shouldSeedSessionBaseline,
  wasLoadingMessages,
}: GetWorkspaceAgentLiveAnnouncementUpdateParams): WorkspaceAgentLiveAnnouncementUpdate => {
  const messageCount = messages.length;
  const lastAnnounceable = getLastWorkspaceAgentLiveAnnouncementMessage(messages);
  const shouldSeedSessionBaselineForUpdate = resolveWorkspaceAgentLiveAnnouncementShouldSeedSessionBaseline({
    isLoadingMessages,
    messageCount,
    shouldSeedSessionBaseline,
    wasLoadingMessages,
  });

  if (!lastAnnounceable) {
    return { action: 'noop', nextLastMessageId: null, nextMessageCount: messageCount };
  }

  const isBulkHistoryLoad = previousMessageCount === 0 && messageCount > 1;

  if (shouldSeedSessionBaselineForUpdate || isBulkHistoryLoad) {
    return { action: 'seed', nextLastMessageId: lastAnnounceable.id, nextMessageCount: messageCount };
  }

  if (previousLastMessageId === null) {
    return {
      action: 'announce',
      announcement: buildWorkspaceAgentLiveAnnouncement(lastAnnounceable),
      nextLastMessageId: lastAnnounceable.id,
      nextMessageCount: messageCount,
    };
  }

  if (lastAnnounceable.id === previousLastMessageId) {
    return { action: 'noop', nextLastMessageId: previousLastMessageId, nextMessageCount: messageCount };
  }

  if (isOptimisticUserMessageHandoff({ previousLastMessageId, nextMessage: lastAnnounceable })) {
    return { action: 'seed', nextLastMessageId: lastAnnounceable.id, nextMessageCount: messageCount };
  }

  return {
    action: 'announce',
    announcement: buildWorkspaceAgentLiveAnnouncement(lastAnnounceable),
    nextLastMessageId: lastAnnounceable.id,
    nextMessageCount: messageCount,
  };
};
